import { Object3D, Vector3 } from "three";
import { PlayEventType, TrackedEvent } from "../../lib/types/statsApi";
import { CameraLandmark, CameraLandmarkLookup } from "../camera/controller";
import { Context, PoserMode } from "../context";
import { Actor } from "../world/actor/actor";
import { EventList } from "../util/events";
import { Frames } from "../util/frames";

export enum DefensivePlayMode {
  BEGIN = "idle",
  PITCHING = "pitching",
  HIT = "hit",
  THROWING = "throwing",
  FIELDING = "fielding",
}

export enum OffensivePlayMode {
  IDLE = "idle",
  BATTING = "batting",
  RUNNING = "running",
}

export abstract class PositionOfInterest {
  protected contextRef: Context;

  constructor(context: Context) {
    this.contextRef = context;
  }
  abstract get position(): Vector3 | undefined;
  abstract get object(): Object3D | undefined;

  abstract equal(other?: PositionOfInterest): boolean;
  abstract clone(): PositionOfInterest;
  abstract toString(): string;
}

export class ActorOfInterest extends PositionOfInterest {
  id: number = -1;

  constructor(context: Context, id: number) {
    super(context);
    this.id = id;
  }

  get position(): Vector3 | undefined {
    return this.actor?.centroid.position;
  }

  get object(): Object3D | undefined {
    return this.actor?.centroid;
  }

  private get actor(): Actor | undefined {
    return this.contextRef?.world?.actors.getActor(this.id);
  }

  equal(other?: PositionOfInterest): boolean {
    if (!(other instanceof ActorOfInterest)) {
      return false;
    }

    return this.id == other.id;
  }

  clone(): PositionOfInterest {
    return new ActorOfInterest(this.contextRef, this.id);
  }

  toString(): string {
    return `actor ${this.id}`;
  }
}

export class LocationOfInterest extends PositionOfInterest {
  private _location: CameraLandmark;
  private _position?: Vector3;
  private _object = new Object3D();

  constructor(context: Context, location: CameraLandmark) {
    super(context);
    this._location = location;
    this.location = this._location;
  }

  set location(location: CameraLandmark) {
    this._location = location;
    this._position = CameraLandmarkLookup[location];
    this._object.position.copy(this._position);
  }

  get position(): Vector3 | undefined {
    return this._position;
  }

  get object(): Object3D | undefined {
    return this._object;
  }

  equal(other?: PositionOfInterest): boolean {
    if (!(other instanceof LocationOfInterest)) {
      return false;
    }

    return this._location == other._location;
  }

  clone(): PositionOfInterest {
    return new LocationOfInterest(this.contextRef, this._location);
  }

  toString(): string {
    return `location ${this._location}`;
  }
}

export class BallOfInterest extends PositionOfInterest {
  private _group?: Object3D;

  constructor(context: Context) {
    super(context);

    this._group = context.world?.ball.ball;
  }

  get position(): Vector3 | undefined {
    return this._group?.position;
  }

  get object(): Object3D | undefined {
    return this._group;
  }

  equal(other?: PositionOfInterest): boolean {
    if (!(other instanceof BallOfInterest)) {
      return false;
    }

    return this._group == other._group;
  }

  clone(): PositionOfInterest {
    return new BallOfInterest(this.contextRef);
  }

  toString(): string {
    return `ball ${this._group ? "OK" : "NONE"}`;
  }
}

abstract class PlayState<T> {
  abstract equal(other: T): boolean;
  abstract clone(): T;
}

export class OffensivePlayState extends PlayState<OffensivePlayState> {
  mode: OffensivePlayMode;
  posOfInterest?: PositionOfInterest;
  ballOfInterest?: BallOfInterest;

  constructor(mode: OffensivePlayMode) {
    super();
    this.mode = mode;
    this.posOfInterest = {} as PositionOfInterest;
    this.ballOfInterest = {} as BallOfInterest;
  }

  equal(other: OffensivePlayState): boolean {
    return this.mode == other.mode && this.posOfInterest == other.posOfInterest;
  }

  clone(): OffensivePlayState {
    const clone = new OffensivePlayState(this.mode);
    clone.posOfInterest = this.posOfInterest;
    return clone;
  }
}

export class DefensivePlayState extends PlayState<DefensivePlayState> {
  mode: DefensivePlayMode;
  posOfInterest: {
    primary?: PositionOfInterest;
    secondary?: PositionOfInterest;
  };

  constructor(mode: DefensivePlayMode) {
    super();
    this.mode = mode;
    this.posOfInterest = {};
  }

  equal(other: DefensivePlayState): boolean {
    return (
      this.mode == other.mode &&
      !!this.posOfInterest.primary?.equal(other.posOfInterest.primary) &&
      !!this.posOfInterest.secondary?.equal(other.posOfInterest.secondary)
    );
  }

  clone(): DefensivePlayState {
    const clone = new DefensivePlayState(this.mode);
    clone.posOfInterest.primary = this.posOfInterest.primary?.clone();
    clone.posOfInterest.secondary = this.posOfInterest.secondary?.clone();
    return clone;
  }
}

export type PlayStateManagerCb = (
  defense: DefensivePlayState,
  offense: OffensivePlayState
) => void;

export class PlayStateManager {
  private defense = new DefensivePlayState(DefensivePlayMode.PITCHING);
  private offense = new OffensivePlayState(OffensivePlayMode.BATTING);

  private subscribers: Map<string, PlayStateManagerCb> = new Map();

  private seenInningEnd = false;
  private lastFrameNum: number | undefined = undefined;

  private contextRef: Context;

  constructor(context: Context) {
    this.contextRef = context;

    this.defense.posOfInterest.primary = new ActorOfInterest(context, 1);
    this.offense.posOfInterest = new ActorOfInterest(context, 10);

    if (context.playStateManager) {
      for (const [name, cb] of context.playStateManager.subscribers) {
        this.addSubscriber(name, cb);
      }
    }
  }

  update(frames: Frames, eventStack: EventList) {
    const current = frames.current;
    if (!current || this.lastFrameNum == current.num) {
      return;
    }

    const updated = this.processEvents(frames, eventStack);

    // let all subscribers know
    if (updated) {
      for (const [name, cb] of this.subscribers) {
        cb(this.defense, this.offense);
      }
    }

    this.lastFrameNum = current.num;
  }

  /**
   * Run through frames not seen and process events
   * @param frames
   * @param eventStack
   * @returns updated bool
   */
  private processEvents(frames: Frames, eventStack: EventList): boolean {
    if (!frames.current) return false;

    const events: TrackedEvent[] = [];

    const prevStateD = this.defense.clone();
    const prevStateO = this.offense.clone();

    for (const frame of [...frames.missed, frames.current]) {
      if (frame.trackedEvents == undefined || frame.trackedEvents.length == 0) {
        continue;
      }

      events.push(...frame.trackedEvents);
    }

    const inInning = this.contextRef.gameState.inningOngoing();
    const inPlay = this.contextRef.gameState.playOngoing();

    if (!inInning) {
      if (this.seenInningEnd) {
        return false;
      } else if (inPlay) {
        // Trigger EOP if we didn't see one already
        events.push({ eventTypeId: PlayEventType.END_OF_PLAY } as TrackedEvent);
      }

      this.seenInningEnd = true;
    } else {
      this.seenInningEnd = false;
    }

    if (events.length == 0) {
      return false;
    }

    // TODO: add back under debug flag - useful for debugging event triggering
    // console.warn(
    //   `inInning: ${inInning} events: ${events.map(
    //     (event) => `    ${event.eventType} pos: ${event.position}`
    //   )}`
    // );

    for (let i = 0; i < events.length; i++) {
      const event = events[i];

      switch (event.eventTypeId) {
        case PlayEventType.BEGIN_OF_PLAY:
        case PlayEventType.PITCHER_FIRST_MOVEMENT:
          this.defense.mode = DefensivePlayMode.BEGIN;
          break;

        case PlayEventType.BALL_WAS_PITCHED:
          this.defense.mode = DefensivePlayMode.PITCHING;
          this.defense.posOfInterest.primary = new ActorOfInterest(
            this.contextRef,
            1
          );
          this.defense.posOfInterest.secondary = undefined;
          break;

        case PlayEventType.BALL_WAS_HIT:
          this.defense.mode = DefensivePlayMode.HIT;
          this.defense.posOfInterest.primary = new ActorOfInterest(
            this.contextRef,
            10
          );
          break;

        case PlayEventType.BALL_WAS_RELEASED: {
          let thrower = event.position;
          let catcher: number | undefined = undefined;

          eventStack.lookAhead(
            event,
            [PlayEventType.BALL_WAS_CAUGHT, PlayEventType.BALL_WAS_FIELDED],
            (found: TrackedEvent) => {
              catcher = found.position;
            }
          );

          // TODO: is there a better way to do this?
          // exclude throws from catcher to pitcher - often happen before EOP tag
          if (!(catcher == 1 && thrower == 2)) {
            this.defense.mode = DefensivePlayMode.THROWING;
            this.defense.posOfInterest.primary = new ActorOfInterest(
              this.contextRef,
              thrower
            );
            if (catcher && catcher != thrower) {
              this.defense.posOfInterest.secondary = new ActorOfInterest(
                this.contextRef,
                catcher
              );
            } else {
              this.defense.posOfInterest.secondary = new BallOfInterest(
                this.contextRef
              );
            }
          }

          break;
        }

        case PlayEventType.BALL_WAS_CAUGHT:
        case PlayEventType.BALL_WAS_CAUGHT_OUT:
        case PlayEventType.BALL_WAS_FIELDED:
          this.defense.mode = DefensivePlayMode.FIELDING;
          this.defense.posOfInterest.primary = new ActorOfInterest(
            this.contextRef,
            event.position
          );
          // TODO: look ahead to label secondary with subsequent caught event?
          eventStack.lookAhead(
            event,
            [PlayEventType.BALL_WAS_CAUGHT, PlayEventType.BALL_WAS_FIELDED],
            (found: TrackedEvent) => {
              if (found.position != event.position) {
                this.defense.posOfInterest.secondary = new ActorOfInterest(
                  this.contextRef,
                  found.position
                );
              } else {
                // 2nd camera target equal to 1st
                this.defense.posOfInterest.secondary = undefined;
              }
            },
            () => {
              this.defense.posOfInterest.secondary = undefined;
            }
          );
          break;

        case PlayEventType.BALL_WAS_HIT:
          eventStack.lookAhead(
            event,
            [
              PlayEventType.BALL_WAS_CAUGHT,
              PlayEventType.BALL_WAS_CAUGHT_OUT,
              PlayEventType.BALL_WAS_FIELDED,
              PlayEventType.BALL_WAS_RELEASED,
            ],
            (found: TrackedEvent) => {
              this.defense.mode = DefensivePlayMode.HIT;
              this.defense.posOfInterest.primary = new ActorOfInterest(
                this.contextRef,
                found.position
              );
              this.defense.posOfInterest.secondary = new BallOfInterest(
                this.contextRef
              );
            },
            // TODO: remove this
            () => console.error("no field event found")
          );
          break;

        case PlayEventType.END_OF_PLAY:
          this.defense.mode = DefensivePlayMode.BEGIN;
          this.defense.posOfInterest.primary = new ActorOfInterest(
            this.contextRef,
            1
          );
          this.defense.posOfInterest.secondary = undefined;
          break;

        default:
          break;
      }

      switch (event.eventTypeId) {
        case PlayEventType.BEGIN_OF_PLAY:
          this.offense.mode = OffensivePlayMode.BATTING;
          this.offense.posOfInterest = new ActorOfInterest(this.contextRef, 10);
          break;

        case PlayEventType.BALL_WAS_HIT:
          this.offense.mode = OffensivePlayMode.RUNNING;
          this.offense.posOfInterest = new ActorOfInterest(this.contextRef, 10);
          this.offense.ballOfInterest = new BallOfInterest(this.contextRef);
          break;

        case PlayEventType.END_OF_PLAY:
          this.offense.mode = OffensivePlayMode.IDLE;
          this.offense.posOfInterest = new ActorOfInterest(this.contextRef, 10);
          break;

        default:
          break;
      }
    }

    return !prevStateD.equal(this.defense) || !prevStateO.equal(this.offense);
  }

  // TODO: add selective registering here - i.e. only register for def or off changes
  addSubscriber(name: string, cb: PlayStateManagerCb) {
    cb(this.defense, this.offense);
    this.subscribers.set(name, cb);
  }

  removeSubscriber(name: string) {
    this.subscribers.delete(name);
  }

  // private processFrames(frames: Frames) {
  //   let updated = false;
  //   for (const frame of [...frames.missed, frames.current]) {
  //     const count = frame?.actorPoses.filter(
  //       // TODO: resurrect me
  //       // ({ ids }) => ids.position >= 1 && ids.position <= 9
  //       () => true
  //     ).length;

  //     const newState =
  //       count !== undefined && count <= 4 ? GameMode.WARMUP : GameMode.LIVE;
  //     updated = this.gameMode !== newState;

  //     // debounce gameMode changes
  //     if (
  //       updated &&
  //       (this.gameMode == undefined ||
  //         this.gameModeChangeCnt.current++ >= this.gameModeChangeCnt.max)
  //     ) {
  //       this.gameMode = newState;
  //       this.gameModeChangeCnt.current = 0;
  //     }

  //     if (updated) break;
  //   }

  //   if (!updated) {
  //     this.gameModeChangeCnt.current = 0;
  //   }
  //   return updated;
  // }

  // TODO: make consistent with other classes and add init() method
  // static makeManager(context: Context) {
  //   const playStateManager = new PlayStateManager(context);

  //   let prevMode: GameMode | undefined = undefined;
  //   playStateManager.addSubscriber(
  //     "gameMode",
  //     (_defense, _offense, gameMode) => {
  //       if (!gameMode || gameMode == prevMode) return;
  //       const transition =
  //         context.options.playbackMode === PoserMode.PLAY
  //           ? "none"
  //           : gameMode === GameMode.WARMUP
  //           ? "out"
  //           : "in";
  //       Actor.actorDefaultTransition = transition;
  //       if (context.world?.actors) {
  //         for (const actor of context.world.actors.getActors()) {
  //           actor.setTransition(transition);
  //         }
  //       }
  //       prevMode = gameMode;
  //     }
  //   );

  //   return playStateManager;
  // }
}
